import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { Slider } from '@/components/Slider';
import { RATE_LIMIT_FIXED_VALUES } from '@/config/rateLimitConfig';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { ScanProfileModalFooter } from '@/sections/settings/components/primitives/ScanProfileModalFooter';

type RateLimitFormState = {
  simultaneousHosts?: number;
  capabilityRateLimit?: number;
};

const UNLIMITED_VALUE = 999999;

interface RateLimitingFormProps {
  customerEmail?: string;
  onClose?: () => void;
}

interface RateLimitingFormInnerProps {
  customerEmail?: string;
  onClose?: () => void;
  initialSimultaneousHosts: number;
  initialCapabilityRateLimit: number;
  rawCurrentRateLimitFromApi: RateLimitFormState | undefined;
}

const RateLimitingFormInner: React.FC<RateLimitingFormInnerProps> = ({
  customerEmail,
  onClose,
  initialSimultaneousHosts,
  initialCapabilityRateLimit,
  rawCurrentRateLimitFromApi,
}) => {
  const { mutateAsync: updateSetting, status: updateSettingStatus } =
    useUpdateSetting(customerEmail);

  const [simultaneousHostsValue, setSimultaneousHostsValue] = useState<number>(
    initialSimultaneousHosts
  );
  const [capabilityRateLimitValue, setCapabilityRateLimitValue] =
    useState<number>(initialCapabilityRateLimit);

  const enableApply = useMemo(() => {
    if (updateSettingStatus === 'pending') {
      return false;
    }

    const apiSimultaneousHosts = rawCurrentRateLimitFromApi?.simultaneousHosts;
    const apiCapabilityRateLimit =
      rawCurrentRateLimitFromApi?.capabilityRateLimit;

    const currentSimultaneousHosts =
      simultaneousHostsValue === UNLIMITED_VALUE
        ? undefined
        : simultaneousHostsValue;
    const currentCapabilityRateLimit =
      capabilityRateLimitValue === UNLIMITED_VALUE
        ? undefined
        : capabilityRateLimitValue;

    return (
      apiSimultaneousHosts !== currentSimultaneousHosts ||
      apiCapabilityRateLimit !== currentCapabilityRateLimit
    );
  }, [
    updateSettingStatus,
    rawCurrentRateLimitFromApi,
    simultaneousHostsValue,
    capabilityRateLimitValue,
  ]);

  const handleApply = async () => {
    try {
      const payload: RateLimitFormState = {};

      if (simultaneousHostsValue !== UNLIMITED_VALUE) {
        payload.simultaneousHosts = simultaneousHostsValue;
      }

      if (capabilityRateLimitValue !== UNLIMITED_VALUE) {
        payload.capabilityRateLimit = capabilityRateLimitValue;
      }

      await updateSetting({
        name: 'rate-limit',
        value: payload,
        silent: true,
      });

      toast.success('Rate limiting updated successfully');
      onClose?.();
    } catch (e) {
      toast.error('Failed to update rate limit', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  };

  const isLoading = updateSettingStatus === 'pending';

  return (
    <div className="flex flex-col gap-8" data-testid="rate-limiting-form">
      <div className="space-y-8">
        <div className="border-chariot-stroke border-b pb-8">
          <Slider
            min={RATE_LIMIT_FIXED_VALUES.SLOW_SCAN_SIMULTANEOUS_HOSTS}
            max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_SIMULTANEOUS_HOSTS}
            value={simultaneousHostsValue}
            onChange={setSimultaneousHostsValue}
            enableUnlimited={true}
            unlimitedLabel={'System Default (Up to 1500)'}
            title="Maximum simultaneous hosts scanned"
            subtitle="Controls how many assets are scanned in parallel."
            minTooltip="Need to scan more gently? Create a per-asset scan profile in Scan Profiles to set a lower limit for specific assets or seeds."
            data-testid="rate-limit-simultaneous-hosts-slider"
          />
        </div>
        <div>
          <Slider
            min={RATE_LIMIT_FIXED_VALUES.SLOW_SCAN_REQUESTS_PER_HOST}
            max={RATE_LIMIT_FIXED_VALUES.FULL_RATE_REQUESTS_PER_HOST}
            value={capabilityRateLimitValue}
            onChange={setCapabilityRateLimitValue}
            enableUnlimited={true}
            unlimitedLabel={'System Default (Up to 300 rq/s)'}
            title="Maximum rate of requests per host"
            subtitle="Controls scanning intensity per individual asset."
            unit="rq/s"
            minTooltip="Need to scan more gently? Create a per-asset scan profile in Scan Profiles to set a lower request rate for specific assets or seeds."
            data-testid="rate-limit-requests-per-host-slider"
          />
        </div>
      </div>
      <ScanProfileModalFooter
        primary={{
          label: 'Save',
          onClick: handleApply,
          disabled: !enableApply || isLoading,
          'data-testid': 'rate-limit-apply-button',
        }}
        cancel={
          onClose
            ? {
                onClick: onClose,
                disabled: isLoading,
                'data-testid': 'rate-limit-cancel-button',
              }
            : undefined
        }
      />
    </div>
  );
};

export const RateLimitingForm: React.FC<RateLimitingFormProps> = ({
  customerEmail,
  onClose,
}) => {
  const { data, status } = useMy(
    { resource: 'setting' },
    customerEmail ? { email: customerEmail } : undefined
  );

  if (status !== 'success') {
    return <Loader isLoading type="spinner" className="h-48" />;
  }

  const rawCurrentRateLimitFromApi = data?.find(
    setting => setting.name === 'rate-limit'
  )?.value as RateLimitFormState | undefined;

  const initialSimultaneousHosts =
    rawCurrentRateLimitFromApi?.simultaneousHosts ?? UNLIMITED_VALUE;
  const initialCapabilityRateLimit =
    rawCurrentRateLimitFromApi?.capabilityRateLimit ?? UNLIMITED_VALUE;

  return (
    <RateLimitingFormInner
      key={`${initialSimultaneousHosts}-${initialCapabilityRateLimit}`}
      customerEmail={customerEmail}
      onClose={onClose}
      initialSimultaneousHosts={initialSimultaneousHosts}
      initialCapabilityRateLimit={initialCapabilityRateLimit}
      rawCurrentRateLimitFromApi={rawCurrentRateLimitFromApi}
    />
  );
};
